"use client";

/**
 * Horizontal marquee with a CONSTANT PERCEIVED SPEED, whatever it is carrying.
 *
 * THE BUG THIS FIXES. `speed` was seconds-per-loop, so the same number meant a different velocity
 * for every marquee. The workspace strip on the homepage ran 29 names at `text-5xl` through the
 * same 64s loop the source pills use for small chips, which made it travel roughly three times
 * faster than the sources marquee beside it and read as urgent rather than ambient.
 *
 * WHAT IT DOES NOW. After mount it measures one copy of the content and sets the duration to
 * `width / PX_PER_SECOND`, so a long strip takes proportionally longer and every marquee on the
 * site moves at the same rate. Re-measures on resize, because the content reflows with the
 * viewport.
 *
 * HYDRATION-SAFE. The server and the first client render both use the `speed` prop verbatim, so
 * the markup matches; the measured value is only applied afterwards, from an effect. This is the
 * `useState`-initialiser trap the rest of the codebase already avoids.
 *
 * `prefers-reduced-motion` is honoured in globals.css, which stops `.marquee-track` outright. The
 * measurement still runs and is simply never visible, which is correct and costs nothing.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/** Pixels per second. Matched to `.marquee-row` (the sources marquee), the one that reads right. */
const PX_PER_SECOND = 45;

/** `useLayoutEffect` warns when it runs on the server; fall back to `useEffect` there. */
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

type Props = {
  children: React.ReactNode;
  className?: string;
  reverse?: boolean;
  /** Fallback seconds-per-loop, used for the server render and if measurement fails. */
  speed?: number;
};

export function Marquee({ children, className, reverse = false, speed = 40 }: Props) {
  const setRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState<number>(speed);

  useIsomorphicLayoutEffect(() => {
    const el = setRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.scrollWidth;
      // A zero width means fonts or images have not settled yet; keep the fallback rather than
      // setting an absurd duration.
      if (w > 0) setDuration(Math.max(8, w / PX_PER_SECOND));
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // Web fonts change the width after they load, and the strip is type-heavy.
    if (typeof document !== "undefined" && "fonts" in document) {
      (document as Document & { fonts: FontFaceSet }).fonts.ready.then(measure).catch(() => {});
    }
    return () => ro.disconnect();
  }, []);

  return (
    <div className={cn("relative w-full overflow-hidden marquee-mask", className)}>
      <div
        className="marquee-track gap-12 py-6"
        style={{
          animationDuration: `${duration}s`,
          animationDirection: reverse ? "reverse" : "normal",
        }}
      >
        <div ref={setRef} className="flex items-center gap-12 px-6">{children}</div>
        {/* Visual duplicate for the seamless loop — inert so its (possibly
            focusable) clones leave the tab order + a11y tree. */}
        <div aria-hidden inert className="flex items-center gap-12 px-6">{children}</div>
      </div>
    </div>
  );
}
